import { useState, useEffect } from 'react';
import { AlertTriangle, CheckCircle, X, ExternalLink } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface DeliverabilityIssue {
  id: string;
  created_at: string;
  email_address: string;
  domain: string;
  issue_type: string;
  issue_details: any;
  status: 'active' | 'resolved' | 'ignored';
  resolved_at?: string;
  notes?: string;
  user_email?: string;
  username_slug?: string;
  similar_issues_count?: number;
}

export default function SendGridDeliverabilityIssues() {
  const [loading, setLoading] = useState(true);
  const [issues, setIssues] = useState<DeliverabilityIssue[]>([]);
  const [filter, setFilter] = useState<'all' | 'active' | 'resolved'>('active');
  const [selectedIssue, setSelectedIssue] = useState<DeliverabilityIssue | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [notes, setNotes] = useState('');
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    loadIssues();
  }, [filter]);

  const loadIssues = async () => {
    try {
      setLoading(true);

      let query = supabase
        .from('admin_deliverability_issues')
        .select('*')
        .order('created_at', { ascending: false });

      if (filter === 'active') {
        query = query.eq('status', 'active');
      } else if (filter === 'resolved') {
        query = query.in('status', ['resolved', 'ignored']);
      }

      const { data, error } = await query;

      if (error) throw error;

      setIssues(data || []);
    } catch (error) {
      console.error('Error loading deliverability issues:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateIssueStatus = async (issueId: string, status: 'resolved' | 'ignored', noteText?: string) => {
    try {
      setUpdating(true);

      const { error } = await supabase
        .from('sendgrid_deliverability_issues')
        .update({
          status,
          resolved_at: new Date().toISOString(),
          notes: noteText || null,
        })
        .eq('id', issueId);

      if (error) throw error;

      await loadIssues();
      setShowDetailsModal(false);
      setSelectedIssue(null);
      setNotes('');
    } catch (error) {
      console.error('Error updating issue:', error);
      alert('Fout bij het updaten van de issue');
    } finally {
      setUpdating(false);
    }
  };

  const viewDetails = (issue: DeliverabilityIssue) => {
    setSelectedIssue(issue);
    setNotes(issue.notes || '');
    setShowDetailsModal(true);
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('nl-NL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getIssueTypeBadge = (issueType: string) => {
    const styles: Record<string, string> = {
      'ip_blocklist': 'bg-red-100 text-red-800',
      'dns_issue': 'bg-yellow-100 text-yellow-800',
      'reputation': 'bg-orange-100 text-orange-800',
    };

    const labels: Record<string, string> = {
      'ip_blocklist': 'IP Blocklist',
      'dns_issue': 'DNS Issue',
      'reputation': 'Reputatie',
    };

    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${styles[issueType] || 'bg-gray-100 text-gray-800'}`}>
        {labels[issueType] || issueType}
      </span>
    );
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      'active': 'bg-red-100 text-red-800',
      'resolved': 'bg-green-100 text-green-800',
      'ignored': 'bg-gray-100 text-gray-800',
    };

    const labels: Record<string, string> = {
      'active': 'Actief',
      'resolved': 'Opgelost',
      'ignored': 'Genegeerd',
    };

    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
        {labels[status] || status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-gray-500">Laden...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900">SendGrid Deliverability Issues</h2>
            <p className="text-sm text-gray-600 mt-1">
              IP blocklist en andere deliverability problemen die admin aandacht vereisen.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setFilter('active')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                filter === 'active'
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Actief
            </button>
            <button
              onClick={() => setFilter('resolved')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                filter === 'resolved'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Opgelost
            </button>
            <button
              onClick={() => setFilter('all')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                filter === 'all'
                  ? 'bg-gray-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Alle
            </button>
          </div>
        </div>

        {issues.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-500" />
            <p className="font-medium">Geen deliverability issues gevonden</p>
            <p className="text-sm mt-1">Alle emails worden succesvol afgeleverd</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-y border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">Datum</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">Email</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">Domain</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">Type</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">Gebruiker</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">Status</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">Actie</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {issues.map((issue) => (
                  <tr key={issue.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-600">
                      {formatDateTime(issue.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-mono text-xs text-gray-900">{issue.email_address}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{issue.domain}</div>
                      {issue.similar_issues_count && issue.similar_issues_count > 1 && (
                        <div className="text-xs text-gray-500 mt-0.5">
                          {issue.similar_issues_count} soortgelijke issues
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {getIssueTypeBadge(issue.issue_type)}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {issue.user_email || '-'}
                    </td>
                    <td className="px-4 py-3">
                      {getStatusBadge(issue.status)}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => viewDetails(issue)}
                        className="text-blue-600 hover:text-blue-800 font-medium"
                      >
                        Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showDetailsModal && selectedIssue && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-start justify-between sticky top-0 bg-white z-10">
              <div>
                <h3 className="text-xl font-bold text-gray-900">Deliverability Issue Details</h3>
                <p className="text-sm text-gray-600 mt-1">{selectedIssue.email_address}</p>
              </div>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <div>{getIssueTypeBadge(selectedIssue.issue_type)}</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <div>{getStatusBadge(selectedIssue.status)}</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Datum</label>
                  <div className="text-sm text-gray-900">{formatDateTime(selectedIssue.created_at)}</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Domain</label>
                  <div className="text-sm text-gray-900">{selectedIssue.domain}</div>
                </div>
              </div>

              {selectedIssue.user_email && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Gebruiker</label>
                  <div className="text-sm text-gray-900">{selectedIssue.user_email}</div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Issue Details</label>
                <div className="bg-gray-50 p-3 rounded border border-gray-200">
                  {selectedIssue.issue_details?.reason && (
                    <div className="mb-2">
                      <span className="font-medium text-gray-700">Reason:</span>
                      <span className="ml-2 text-gray-900">{selectedIssue.issue_details.reason}</span>
                    </div>
                  )}
                  {selectedIssue.issue_details?.response && (
                    <div className="mb-2">
                      <span className="font-medium text-gray-700">Response:</span>
                      <span className="ml-2 text-gray-900">{selectedIssue.issue_details.response}</span>
                    </div>
                  )}
                  {selectedIssue.issue_details?.status && (
                    <div className="mb-2">
                      <span className="font-medium text-gray-700">Status Code:</span>
                      <span className="ml-2 text-gray-900">{selectedIssue.issue_details.status}</span>
                    </div>
                  )}
                  {selectedIssue.issue_details?.bounce_count && (
                    <div>
                      <span className="font-medium text-gray-700">Bounce Count:</span>
                      <span className="ml-2 text-gray-900">{selectedIssue.issue_details.bounce_count}</span>
                    </div>
                  )}
                </div>
              </div>

              {selectedIssue.status === 'active' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notities</label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows={3}
                    placeholder="Voeg notities toe..."
                  />
                </div>
              )}

              {selectedIssue.notes && selectedIssue.status !== 'active' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notities</label>
                  <div className="bg-gray-50 p-3 rounded border border-gray-200 text-sm text-gray-900">
                    {selectedIssue.notes}
                  </div>
                </div>
              )}

              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-blue-900 mb-1">Actie vereist</p>
                    <p className="text-blue-800">
                      Controleer SendGrid dashboard voor IP reputation issues en neem indien nodig contact op met SendGrid support.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {selectedIssue.status === 'active' && (
              <div className="p-6 border-t border-gray-200 flex justify-end gap-3 sticky bottom-0 bg-white">
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  disabled={updating}
                >
                  Annuleren
                </button>
                <button
                  onClick={() => updateIssueStatus(selectedIssue.id, 'ignored', notes)}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                  disabled={updating}
                >
                  Negeren
                </button>
                <button
                  onClick={() => updateIssueStatus(selectedIssue.id, 'resolved', notes)}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  disabled={updating}
                >
                  {updating ? 'Bezig...' : 'Markeer als Opgelost'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
